/**
 * Claims carried by an auth-service customer access token (`JwtIssuer.issueUserToken`).
 *
 * `sub` is the customer's email and `uid` the Somic user id — the same shape Okta's tokens had, which is
 * what let customer-portal-api verify either during the migration. Only auth-service tokens are accepted
 * now (`AuthServiceTokenVerifier`, TED-4206).
 */
export interface AccessTokenClaims {
  sub: string
  uid?: string
  groups: string[]
  clientType?: string
  /** Seconds since the epoch, as issued by auth-service. */
  exp: number
  iat?: number
}

/**
 * Reads the claims out of an access token **without verifying it**.
 *
 * Verification belongs to customer-portal-api, which checks the signature against auth-service's JWKS on
 * every call. The browser decodes only to know when to refresh and who is signed in; nothing here is a
 * security decision, so a forged token gains an attacker nothing but a rejected API call.
 *
 * Returns null for anything that is not a decodable JWT with a numeric `exp`, which the caller treats the
 * same as having no token at all.
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  const payload = decodePayload(token)
  if (!payload) return null

  const { sub, exp } = payload
  if (typeof sub !== 'string' || typeof exp !== 'number') return null

  return {
    sub,
    exp,
    uid: typeof payload.uid === 'string' ? payload.uid : undefined,
    groups: Array.isArray(payload.groups) ? payload.groups.filter(isString) : [],
    // auth-service stamps `client_type`; kept camelCase on this side to match the app's conventions.
    clientType: typeof payload.client_type === 'string' ? payload.client_type : undefined,
    iat: typeof payload.iat === 'number' ? payload.iat : undefined,
  }
}

/**
 * Whether [claims] still has more than [skewSeconds] of life left.
 *
 * The skew is what makes refresh proactive: a token inside the window is treated as already spent so the
 * refresh happens before a call goes out with it, rather than after cpa has rejected one.
 */
export function isFresh(claims: AccessTokenClaims, skewSeconds: number, nowMs: number = Date.now()): boolean {
  return claims.exp - skewSeconds > Math.floor(nowMs / 1000)
}

function decodePayload(token: string): Record<string, unknown> | null {
  const segments = token.split('.')
  if (segments.length !== 3) return null

  try {
    return JSON.parse(base64UrlDecode(segments[1])) as Record<string, unknown>
  } catch {
    return null
  }
}

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = window.atob(padded)
  // Decode as UTF-8 rather than trusting atob's latin1 output, so a name or email with non-ASCII
  // characters in it survives the round trip.
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)))
}

function isString(value: unknown): value is string {
  return typeof value === 'string'
}
